export type TableData = {
  name: string;
  columns: string[];
  rows: Record<string, unknown>[];
};

export const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export function extractTableData(event: {
  content: string;
  metadata?: Record<string, unknown>;
}): TableData | null {
  const toolInput = event.metadata?.toolInput as
    | Record<string, unknown>
    | undefined;

  let source = toolInput ?? tryParseJson(event.content);
  if (!source) return null;

  // Handle nested content wrapper (insights-service history format)
  if (!source.columns && typeof source.content === 'string') {
    const inner = tryParseJson(source.content as string);
    if (inner) source = inner;
  }

  const name = typeof source.name === 'string' ? source.name : '';
  const columns = parseField<unknown[]>(source.columns);
  const rows = parseField<unknown[][]>(source.rows);

  if (!columns || !rows) return null;

  const stringColumns = columns.filter(
    (c): c is string => typeof c === 'string',
  );
  if (stringColumns.length === 0) return null;

  const objectRows = rows.map(row => {
    if (Array.isArray(row)) {
      const obj: Record<string, unknown> = {};
      stringColumns.forEach((col, i) => {
        obj[col] = row[i];
      });
      return obj;
    }
    return row as Record<string, unknown>;
  });

  return { name, columns: stringColumns, rows: objectRows };
}

function parseField<T>(value: unknown): T | null {
  if (Array.isArray(value)) return value as T;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T) : null;
    } catch {
      return null;
    }
  }
  return null;
}

function tryParseJson(text: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(text);
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}
